import useAppStore from '@/store/modules/app'
import useUserStore from '@/store/modules/user'
import { useRoute } from 'vue-router'
import { computed, ref, unref } from 'vue'
import { storeToRefs } from 'pinia'
import { DATA_ANALYSIS_ROOT_PERMISSION, hasAnyPermission } from '@/config/menuPermissionGroups'

function resolveVisible(value) {
  const resolved = unref(value)
  if (typeof resolved === 'function') return resolved()
  return resolved === void 0 || !!resolved
}

function hasPerms(userPerms, perms) {
  return hasAnyPermission(userPerms, perms)
}

function canShowItem(item, userPerms) {
  return resolveVisible(item.visible) && hasPerms(userPerms, item.perms)
}

function hasAnyChildPerms(children = [], userPerms) {
  return children.some(child => canShowItem(child, userPerms))
}

function filterSections(sections = [], userPerms = []) {
  return sections.reduce((out, section) => {
    if (!resolveVisible(section.visible)) return out
    const next = { ...section }
    const sectionAllowed = hasPerms(userPerms, section.perms)

    if (Array.isArray(section.children) && section.children.length) {
      next.children = sectionAllowed
        ? section.children.filter(child => resolveVisible(child.visible))
        : section.children.filter(child => canShowItem(child, userPerms))
      if (!sectionAllowed && !hasAnyChildPerms(section.children, userPerms)) return out
      if (!next.children.length) return out
    } else if (!sectionAllowed) {
      return out
    }

    out.push(next)
    return out
  }, [])
}

export function filterMenuGroupsBySections(groups = [], userPerms = []) {
  return groups.reduce((out, group) => {
    if (!resolveVisible(group.visible)) return out
    if (!group.hover) {
      out.push(group)
      return out
    }
    if (!Array.isArray(group.sections) || !group.sections.length) return out
    const sections = filterSections(group.sections, userPerms)
    if (!sections.length) return out
    out.push({ ...group, sections })
    return out
  }, [])
}

export default function getMenuConfig() {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const { permissions } = storeToRefs(userStore)

  const route = useRoute()
  const stationId = computed(() => route.query.stationId)
  const station = computed(() => appStore.getStationById(stationId.value))

  const pvVisible = computed(() => {
    return !!(station.value?.pvAccess * 1)
  })




  /**
   * 侧栏「全菜单」浮框配置 — 三级结构
   *
   * 一级 (group) → 二级 (section) → 可选三级 (children)
   *
   * 字段说明：
   *  - key/title:   一级
   *  - rootPath:    一级图标 isActive 高亮 / 无浮框时点击直跳
   *  - hover:       是否在 hover 时弹浮框（false 表示点击直接跳转）
   *  - sections:    二级块（一级浮框内横向排列，3 列一行）
   *      - title/desc/path/query/vip/perms：二级本身可点击跳转
   *      - children:    可选三级，存在时二级块内 3 列展示三级
   *  - 三级项字段同二级（title/desc/path/query/vip/perms）
   */
  const menuGroups = ref([
    {
      key: 'dashboard',
      title: '工作台',
      rootPath: '/index',
      hover: false,
      sections: [],
    },
    {
      key: 'stations',
      title: '我的电站',
      rootPath: '/my-powerStation',
      hover: false,
      sections: [],
    },
    {
      key: 'monitor',
      title: '实时监控',
      rootPath: '/station-monitor',
      hover: true,
      sections: [
        { title: '监控总览', desc: '多电站实时态势看板', path: '/station-monitor/summary', perms: ['device:overview:query'] },
        { title: '主监控', desc: '单站遥测与运行状态', path: '/station-monitor/detail', query: { tab: '主监控' }, perms: ['device:realtime:query'] },
        { title: '电站拓扑', desc: '一次接线与设备拓扑视图', path: '/station-monitor/detail', query: { tab: '电站拓扑' }, perms: ['device:topology:query'] },
        { title: '储能系统', desc: '电池簇与 PCS 详情', path: '/station-monitor/detail', query: { tab: '储能系统' }, perms: ['device:storage:query'] },
        { title: '充放记录', desc: '历史充放电流水查询', path: '/station-monitor/detail', query: { tab: '充放记录' }, perms: ['device:chargeRecord:query'] },
        { title: '电表', desc: '远程电表实时读数', path: '/station-monitor/detail', query: { tab: '电表' }, perms: ['device:meter:query'] },
        { title: '光伏', desc: '光伏逆变器实时监控', path: '/station-monitor/detail', query: { tab: '光伏' }, visible: pvVisible, perms: ['device:pv:query'] },
      ],
    },
    {
      key: 'business',
      title: '运营管理',
      rootPath: '/business-operations',
      hover: true,
      sections: [
        { title: '运营总览', desc: '多站收益与 SOC/SOH 概览', path: '/business-operations/summary', perms: ['operate:overview:query'] },
        { title: '收益总览', desc: '单站收益详情与对账', path: '/business-operations/detail', query: { tab: '收益总览' }, perms: ['operate:revenue:query'] },
        { title: '电价录入', desc: '峰平谷电价基础维护', path: '/business-operations/detail', query: { tab: '电价录入' }, perms: ['operate:electricPrice:query'] },
        { title: '抄表结算', desc: '抄表、换表与结算修复', path: '/business-operations/detail', query: { tab: '抄表结算' }, perms: ['operate:meterSettle:query'] },
        { title: '策略控制', desc: '运行策略下发与计划编辑', path: '/business-operations/detail', query: { tab: '策略控制' }, perms: ['ops:strategy:query'] },
        { title: '电表读数', desc: '电表实时读数与抄表', path: '/business-operations/detail', query: { tab: '电表读数' }, perms: ['operate:meterReading:query'] },
        { title: '远程电表', desc: '远程电表参数与读取', path: '/business-operations/detail', query: { tab: '远程电表' }, perms: ['operate:remoteMeter:query'] },
        { title: '电价维护', desc: '时段规则与现货高级维护', path: '/business-operations/detail', query: { tab: '电价维护' }, vip: true, perms: ['operate:electricPriceMaintain:query'] },
         { title: '策略模板', desc: '用户级策略模板批量下发', path: '/business-operations/detail', query: { tab: '策略模板' }, perms: ['ops:strategy:module:query'] },
         { title: '高级配置', desc: '无功/箱变/自动需量高级参数', path: '/business-operations/detail', query: { tab: '高级配置' }, perms: ['ops:strategy:set:query'] },
         { title: '天气数据', desc: '按日期与省市查询天气记录', path: '/business-operations/detail', query: { tab: '天气数据' }, perms: ['operate:weather:query'] },
      ],
    },
    {
      key: 'it',
      title: '运维管理',
      rootPath: '/it-operations',
      hover: true,
      sections: [
        { title: '运维总览', desc: '多站健康度与告警看板', path: '/it-operations/multi/overview', perms: ['ops:overview:query'] },
        { title: '电站管理', desc: '电站基础信息维护', path: '/it-operations/multi/stations', perms: ['ops:station:query'] },
        { title: '告警管理', desc: '告警查询、规则与编码管理', path: '/it-operations/multi/alarm', perms: ['ops:alarm:query'] },
        { title: '告警分析', desc: '跨站告警趋势与规律分析', path: '/it-operations/multi/alarm-analysis', vip: true, perms: ['ops:alarmAnalysis:query'] },
        { title: '客户管理', desc: '投资人与签约信息维护', path: '/it-operations/multi/investors', perms: ['ops:customer:query'] },
        { title: 'OTA 升级', desc: '设备远程固件升级', path: '/it-operations/multi/ota', perms: ['ops:ota:query'] },
        // { title: '智能告警', desc: 'AI 智能告警摘要与分析', path: '/it-operations/detail', query: { tab: '智能告警' }, vip: true, perms: ['ops:smartAlert:query'] },
        { title: '告警信息', desc: '实时/历史告警处理', path: '/it-operations/detail', query: { tab: '告警信息' }, perms: ['ops:alarmInfo:query'] },
        { title: '远程开关机', desc: 'eBlock 远程启停控制', path: '/it-operations/detail', query: { tab: '远程开关机' }, perms: ['ops:params:set'] },
        {
          title: '数据分析',
          desc: '运行特征与曲线深度分析',
          path: '/it-operations/multi/data-analysis',
          perms: [DATA_ANALYSIS_ROOT_PERMISSION],
          children: [
            { title: '功率跟踪', desc: '功率响应曲线', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'powerTrack' }, perms: ['ops:powerTrack:query'] },
            { title: '负荷跟踪', desc: '负荷与预测对比', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'loadTrack' }, perms: ['ops:loadTrack:query'] },
            { title: '电压一致性', desc: '电池簇电压偏差', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'voltageConsistency' }, perms: ['ops:voltageConsistency:query'] },
            { title: '温度热管理', desc: '电池温度监测', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'heat' }, perms: ['ops:temperatureThermal:query'] },
            { title: '需量分析', desc: '最大需量预警', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'demand' }, vip: true, perms: ['ops:demandAnalysis:query'] },
            { title: '电表分析', desc: '用电异常分析', path: '/it-operations/detail', query: { tab: '数据分析', sub: 'meterData' }, perms: ['ops:meterAnalysis:query'] },
          ],
        },
      ],
    },
    {
      key: 'system',
      title: '系统设置',
      rootPath: '/system-manager',
      hover: true,
      sections: [
        { title: '业务配置', desc: '点表与产品型号配置', path: '/system-manager/config', perms: ['system:businessConfig:query'] },
        { title: '菜单配置', desc: '菜单结构与图标维护', path: '/system-manager/menu', perms: ['system:menu:query'] },
        { title: '角色管理', desc: '角色与权限定义', path: '/system-manager/role', perms: ['system:role:query'] },
        { title: '组织管理', desc: '组织架构层级维护', path: '/system-manager/org', perms: ['system:org:query'] },
        { title: '用户管理', desc: '账号与角色分配', path: '/system-manager/user', perms: ['system:user:query'] },
        { title: '操作日志', desc: '系统操作行为审计', path: '/system-manager/operlog', perms: ['monitor:operlog:list'] },
        { title: '登录日志', desc: '用户登录记录查询', path: '/system-manager/logininfor', perms: ['monitor:logininfor:list'] },
      ],
    },
  ])

  const visibleMenuGroups = computed(() => {
    return filterMenuGroupsBySections(menuGroups.value, permissions.value)
  })


  return {
    menuGroups,
    visibleMenuGroups,
  }


}


